import { useCallback, useEffect, useRef, useState } from 'react';
import { useLecteur } from '../lecture/useLecteur';
import { useAccord } from '../sync/useAccord';
import { affiche } from '../lib/images';
import Participants from '../ui/Participants';
import { useParticipants } from '../discord/useParticipants';

/**
 * La séance occupe tout le panneau, et rien d'autre.
 *
 * Un `<video>` posé dans une page qui défile donnait le pire des deux mondes :
 * une image trop petite, et un panneau qu'on fait glisser par accident en
 * cherchant la barre de lecture. Ici la scène est fixée à la fenêtre, l'image
 * s'y inscrit en entier, et le reste vient par-dessus.
 *
 * Deux rôles y cohabitent. Le **maître** commande : ses gestes sont poussés au
 * serveur, qui les répercute. Les **autres** suivent : leur lecture est
 * recalée en continu, doucement quand l'écart est petit, sèchement quand il
 * est grand. Leurs contrôles restent actifs — pour le volume, le plein écran —
 * mais toute pause ou tout saut est repris dans la seconde. C'est déroutant
 * une fois, et parfaitement clair une fois qu'on a lu le bandeau.
 */

const DELAI_REPOS = 3000;

export default function Lecteur({ film, utilisateur, seance, onRetour }) {
  const { proprietes, videoRef, reprise, reprendre, recommencer, erreur } =
    useLecteur({ film, utilisateur });
  const participants = useParticipants();

  const { jeSuisMaitre, maitreAbsent, nomDuMaitre, positionAttendue, pousser, prendreLaMain } = seance;
  const enLectureAttendue = Boolean(seance.etat?.is_playing);

  const sceneRef = useRef(null);
  const minuteurRef = useRef(null);
  const [barreVisible, setBarreVisible] = useState(true);
  const [pleinEcran, setPleinEcran] = useState(false);
  const [aRejoint, setARejoint] = useState(jeSuisMaitre);

  // On ne corrige que les suiveurs, et seulement après leur premier geste :
  // un navigateur refuse de lancer une lecture sonore sans intervention
  // humaine, et forcer avant produirait un film muet ou figé.
  useAccord({
    videoRef,
    actif: !jeSuisMaitre && aRejoint,
    positionAttendue,
    enLecture: enLectureAttendue,
  });

  /* ── Ce que le maître pousse ──────────────────────────────────────── */

  const signaler = useCallback((enLecture) => {
    if (!jeSuisMaitre) return;

    const video = videoRef.current;
    pousser({ is_playing: enLecture, position: video?.currentTime ?? 0 });
  }, [jeSuisMaitre, pousser, videoRef]);

  /* ── Barre qui s'efface ───────────────────────────────────────────── */

  const reveiller = useCallback(() => {
    setBarreVisible(true);
    clearTimeout(minuteurRef.current);

    if (enLectureAttendue && !reprise) {
      minuteurRef.current = setTimeout(() => setBarreVisible(false), DELAI_REPOS);
    }
  }, [enLectureAttendue, reprise]);

  useEffect(() => {
    reveiller();
    return () => clearTimeout(minuteurRef.current);
  }, [reveiller]);

  /* ── Plein écran ──────────────────────────────────────────────────── */

  const pleinEcranPossible = typeof document !== 'undefined' && document.fullscreenEnabled;

  useEffect(() => {
    const surChangement = () => setPleinEcran(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', surChangement);
    return () => document.removeEventListener('fullscreenchange', surChangement);
  }, []);

  const basculerPleinEcran = () => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else sceneRef.current?.requestFullscreen?.().catch(() => {});
  };

  /* ── Rendu ────────────────────────────────────────────────────────── */

  const image = affiche(film.poster);

  const rejoindre = () => {
    setARejoint(true);
    videoRef.current?.play().catch(() => {});
  };

  return (
    <div className="scene" ref={sceneRef} onPointerMove={reveiller} onPointerDown={reveiller}>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        className="video-plein"
        {...proprietes}
        onPlay={(e) => { proprietes.onPlay?.(e); signaler(true); }}
        onPause={(e) => { proprietes.onPause?.(e); signaler(false); }}
        onSeeked={() => { if (jeSuisMaitre) signaler(!videoRef.current?.paused); }}
      />

      <header className={`scene-barre${barreVisible ? '' : ' effacee'}`}>
        {/* Un suiveur ne quitte pas la séance : c'est le maître qui décide du
            film. Afficher un bouton sans effet serait pire que son absence. */}
        {onRetour && (
          <button type="button" className="rond-bouton" onClick={onRetour} aria-label="Retour à la bibliothèque">
            ←
          </button>
        )}

        <div className="scene-titres">
          <b>{film.title}</b>
          <span>
            {seance.projection
              // Dans une projection, personne ne commande : le dire évite de
              // chercher qui, et de croire qu'on pourrait prendre la main.
              ? `Projection · départ à ${seance.heureDeDepart}`
              : jeSuisMaitre
                ? 'Tu contrôles la lecture'
                : `${nomDuMaitre ?? 'Quelqu’un'} contrôle la lecture`}
          </span>
        </div>

        <Participants participants={participants} compact />

        {pleinEcranPossible && (
          <button
            type="button"
            className="rond-bouton"
            onClick={basculerPleinEcran}
            aria-label={pleinEcran ? 'Quitter le plein écran' : 'Plein écran'}
          >
            {pleinEcran ? '⤡' : '⤢'}
          </button>
        )}
      </header>

      {/* Un suiveur qui vient d'arriver : le navigateur exige un geste avant
          de lancer le son, autant en faire le moment où il rejoint. */}
      {!jeSuisMaitre && !aRejoint && (
        <div className="voile">
          {image && <img className="voile-affiche" src={image} alt="" />}
          <p className="voile-titre">{film.title}</p>
          <p className="discret">
            {seance.projection
              ? 'La projection a commencé.'
              : nomDuMaitre ? `${nomDuMaitre} a lancé la séance.` : 'Une séance est en cours.'}
          </p>
          <button type="button" onClick={rejoindre}>Rejoindre la séance</button>
        </div>
      )}

      {/* La main est libre : quelqu'un doit la reprendre pour que ça reparte.
          Sans objet dans une projection, où il n'y a pas de main à prendre. */}
      {!seance.projection && !jeSuisMaitre && maitreAbsent && aRejoint && (
        <div className="bandeau-bas">
          <span>Plus personne ne contrôle la lecture.</span>
          <button type="button" onClick={prendreLaMain}>Prendre la main</button>
        </div>
      )}

      {jeSuisMaitre && reprise && (
        <div className="voile">
          {image && <img className="voile-affiche" src={image} alt="" />}
          <p className="voile-titre">Tu en étais à {Math.round(reprise.percentage)} %</p>
          <div className="actions">
            <button type="button" onClick={reprendre}>Reprendre</button>
            <button type="button" className="secondaire" onClick={recommencer}>Depuis le début</button>
          </div>
        </div>
      )}

      {erreur && (
        <div className="voile">
          <p className="voile-titre">Lecture impossible</p>
          <p className="discret">{erreur}</p>
          {onRetour && <button type="button" onClick={onRetour}>Retour</button>}
        </div>
      )}
    </div>
  );
}
